/**
 * RWBR — the EZRA RACE START EVENT (RSE).
 *
 * Sources, and they are two:
 *   • `EZRA_ Restart Procedure _ver 2[2763].docx` — "RACE START EVENT
 *     PROCEDURES 6/28/2019 (Rev 10/31/2019)". Dedicated to the subject,
 *     dated, and the later of the two.
 *   • `EZRAS2RulesFinal.docx` — "Restart Procedures", a shorter section
 *     carrying the same d10 table.
 *
 * The d10 table is IDENTICAL in both, word for word, including the asymmetric
 * group-selection dice noted below. Everything the ver-2 document adds — the RS
 * trait clause, the performance-chip prohibition, the "reroll on the next
 * higher group" rule — is additive rather than contradictory. So the two agree
 * on everything except one sentence, which is the whole of D-EZ-22.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-22 — THE TRIGGER IS A d10 OF 1. NOTHING ELSE.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The two documents state the activation differently:
 *
 *   ver 2:  *"An RSE is activated by a D10 roll of 1. on the EZRA PRELUDE
 *            TRIGGER TABLE (PTT). (See Rules Appendix.)"*
 *   S2:     *"Activated on any 'Restart Turn' when a prelude D10 Roll = 1
 *            **followed by a positive decider die roll**."*
 *
 * S2 adds a SECOND gate, and that gate is unusable: the **decider die is never
 * defined anywhere in the corpus** — no face count, no read, no meaning
 * (`CROSS_REFERENCE.md` B4) — and "positive" has no meaning without one. It
 * cannot be implemented without inventing a die and then inventing what
 * "positive" means on it, which would be inventing the rate of the entire
 * subsystem.
 *
 * **Ruled: the ver-2 document governs.** Three reasons, in order of weight:
 *
 *   1. **It does not depend on an undefined term.** A rule you can execute
 *      beats a rule you cannot, when both are by the same authors about the
 *      same mechanic.
 *   2. **It is the LATER document.** Rev 10/31/2019 against S2's Appendix C
 *      version stamp of Jan 2017.
 *   3. **It is the document dedicated to the subject**, which is the same
 *      principle D-EZ-13 used to settle the Lucky Dog timing — a section
 *      written about a rule governs over a summary bullet that mentions it.
 *
 * ── AND THE PRELUDE TRIGGER TABLE IS NOT NEEDED FOR THIS ───────────────────
 *
 * `CROSS_REFERENCE.md` B8 records the PTT as missing and lists Restart Events
 * as blocked on it. **Restart Events are not blocked on it**, and it is worth
 * being precise about why, because the entry has been read as blocking three
 * subsystems when it only really blocks one.
 *
 * Both documents state the RSE trigger IN PROSE — "a D10 roll of 1" — so the
 * rate is printed: **one turn in ten, on restart turns only.** The PTT is a
 * lookup that routes a d10 face to whichever prelude is eligible; for the RSE
 * the prose already gives the answer the table would have given.
 *
 * What the PTT *is* genuinely needed for is **Bottom Group Surge**, whose S2
 * text says only *"activated by a D10 roll on the PTT"* with no number at all.
 * That one has no printed rate and is the real casualty of B8. Bottom Group
 * Churn, like the RSE, prints its own "D10 roll of 1".
 *
 * ── ONE PRINTED ASYMMETRY, REPRODUCED RATHER THAN TIDIED ───────────────────
 *
 * The group-selection d6 is NOT the same across rows, in either document:
 *
 *   rows 2-5 and 9   →  1-3 Top · 4-5 Mid · 6 Bottom
 *   rows 7, 8 and 10 →  1-2 Top · 3-4 Mid · 5-6 Bottom
 *
 * Restart violations and driver errors are therefore twice as likely to land in
 * the TOP group as problems and trouble are. Both documents print it that way,
 * identically, so it is a design choice and not a typo — and it is exactly the
 * kind of detail a tidy-minded implementation would smooth away without
 * noticing.
 */

import type { Dice } from './dice.ts';
import type { DiceRecord, Modifier, PositionDelta, RuleEvent } from './ruleEvent.ts';
import type { TroubleField, TroubleGroup } from './trouble.ts';
import { rollDriverFinder, type DriverFinderResult } from './problem.ts';

export const CITE_RSE =
  'EZRA_ Restart Procedure ver 2 (Rev 10/31/2019) — "RACE START EVENT PROCEDURES". The same d10 ' +
  'table is printed in EZRAS2RulesFinal.docx under "Restart Procedures".';
export const CITE_RSE_TRIGGER =
  'D-EZ-22 — "An RSE is activated by a D10 roll of 1." (ver 2). EZRA-S2\'s extra gate ("followed by ' +
  'a positive decider die roll") is REJECTED because the decider die is undefined corpus-wide ' +
  '(CROSS_REFERENCE B4) and "positive" has no meaning without it.';
export const CITE_RSE_WHEN =
  'EZRA-RSE — "RSE\'s occur prior to turns that immediately follow yellow flag turns, turns where ' +
  'restarts are required after a play board result or other event and before the first turn of a race."';
export const CITE_RSE_CHIPS =
  'EZRA-RSE — "Performance Chips are treated as though the RSE is a problem event and their use is ' +
  'prohibited (RB/xiv). However, a driver can use a PC to avoid Trouble (RB/xiv) that arises out of ' +
  'the RSE."';
export const CITE_RS_TRAIT =
  'EZRA-RSE — "A driver with the RS Trait when individually designated on the Race Start Result ' +
  'Table ignores any negative result. This ends the RSE."';

/** "An RSE is activated by a D10 roll of 1." One turn in ten. */
export const RSE_TRIGGER_FACE = 1;
export const RSE_TRIGGER_SIDES = 10;

// ---------------------------------------------------------------------------
// When a turn is a restart turn
// ---------------------------------------------------------------------------

export interface RestartTurnInput {
  turn: number;
  /** Was the PREVIOUS turn run under a yellow flag? */
  previousWasYellow: boolean;
  /** Is THIS turn itself a yellow-flag turn? */
  thisIsYellow: boolean;
  /** Is THIS turn a scheduled pit turn? */
  thisIsPitTurn: boolean;
  /** Was the previous turn a pit turn that ran under yellow? */
  previousWasYellowPitTurn: boolean;
}

/**
 * Is an RSE even eligible on this turn?
 *
 * The printed cases, and the two worked examples are reproduced as tests:
 *
 *   • before the first turn of a race;
 *   • prior to a turn that immediately follows a yellow flag turn;
 *   • *"An RSE is executed on the first turn following a Pit Stop Turn that
 *     occurred during a Yellow Flag Turn"* — yellow on 6 makes the turn-7 pit
 *     stop a yellow turn, so turn 8 activates;
 *   • *"An RSE is activated at the beginning of a Pit Turn that immediately
 *     follows a Yellow Flag Turn"* — yellow on 5, turn 6 yellow, turn 7 is a
 *     pit turn and the RSE fires before it.
 *
 * A turn run UNDER yellow is never itself a restart — you cannot restart while
 * the caution is still out.
 */
export function isRestartTurn(input: RestartTurnInput): boolean {
  if (input.thisIsYellow) return false;
  if (input.turn === 1) return true;
  if (input.previousWasYellow) return true;
  if (input.previousWasYellowPitTurn) return true;
  return false;
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

export type RseKind =
  | 'shaky'
  | 'violation'
  | 'great'
  | 'problem'
  | 'trouble'
  | 'driver-error'
  | 'roughed-up';

export interface RseRow {
  /** The d10 faces this row covers. */
  faces: readonly number[];
  kind: RseKind;
  /** The result column, verbatim. */
  text: string;
  /**
   * The group-selection d6, as printed. NOT uniform across rows — see the
   * header. `null` means the row names a group outright.
   */
  groupDice: Readonly<Record<number, TroubleGroup>> | null;
  /** True when the RS trait cancels it ("ignores any negative result"). */
  negative: boolean;
}

/**
 * The two printed group splits. Exported so a test can assert they are actually
 * DIFFERENT tables rather than two names for one — which is the failure a tidy
 * implementation produces and nothing else would catch.
 */
export const D6_GROUP_SPLITS = Object.freeze({
  /** 1-3 Top · 4-5 Mid · 6 Bottom — rows 2-5 and 9. */
  threeTwoOne: Object.freeze({
    1: 'top', 2: 'top', 3: 'top', 4: 'middle', 5: 'middle', 6: 'bottom',
  }) as Readonly<Record<number, TroubleGroup>>,
  /** 1-2 Top · 3-4 Mid · 5-6 Bottom — rows 7, 8 and 10. */
  twoTwoTwo: Object.freeze({
    1: 'top', 2: 'top', 3: 'middle', 4: 'middle', 5: 'bottom', 6: 'bottom',
  }) as Readonly<Record<number, TroubleGroup>>,
});
const D6_THREE_TWO_ONE = D6_GROUP_SPLITS.threeTwoOne;
const D6_TWO_TWO_TWO = D6_GROUP_SPLITS.twoTwoTwo;

export const RSE_TABLE: readonly RseRow[] = [
  {
    faces: [1],
    kind: 'shaky',
    text: 'Shaky restart unsettles the field.',
    groupDice: null,
    // It hits the whole field rather than a driver, so there is nobody for the
    // RS trait to be "individually designated" as.
    negative: true,
  },
  {
    faces: [2, 3, 4, 5],
    kind: 'violation',
    text: 'Restart Violation (Note 1)',
    groupDice: D6_THREE_TWO_ONE,
    negative: true,
  },
  {
    faces: [6],
    kind: 'great',
    text: 'Great restart. (Note 2)',
    groupDice: null, // "Use Top Group Driver Finder Table."
    negative: false,
  },
  { faces: [7], kind: 'problem', text: 'Problem', groupDice: D6_TWO_TWO_TWO, negative: true },
  {
    faces: [8],
    kind: 'trouble',
    text: 'Trouble (Note 3) Roll d10 as below',
    groupDice: D6_TWO_TWO_TWO,
    negative: true,
  },
  {
    faces: [9],
    kind: 'driver-error',
    text: 'Driver error. Lose 10 positions.',
    groupDice: D6_THREE_TWO_ONE,
    negative: true,
  },
  {
    faces: [10],
    kind: 'roughed-up',
    text: 'Roughed up in traffic. Position loss is 2D10 added (Minimum loss of 4 spots).',
    groupDice: D6_TWO_TWO_TWO,
    negative: true,
  },
];

export function rseRowFor(face: number): RseRow {
  const row = RSE_TABLE.find((r) => r.faces.includes(face));
  if (!row) throw new RangeError(`RSE: no row for d10 face ${face}`);
  return row;
}

/** Note 1 — "Restart Violations incur a stop and go penalty." */
export const CITE_NOTE_1 =
  'EZRA-RSE Note 1 — "Restart Violations incur a stop and go penalty. Top Group offenders displace ' +
  'to the last position in the middle group. Others to the last position in the Bottom Group Lead ' +
  'Lap Sub-Group. Open spaces are filled immediately and automatically. This event is resolved ' +
  'prior to regular turn activity."';

/** Note 2 — the only POSITIVE result on the table. */
export const CITE_NOTE_2 =
  'EZRA-RSE Note 2 — "Great restart. If either the Leader or second place car is designated on the ' +
  'Top Group Driver Finder Table award a performance chip. If the designated car is in 3rd through ' +
  '7th place, it advances to second place. The Second-place car displaces to the vacated spot."';

/** Note 3 — the Trouble sub-roll. */
export const CITE_NOTE_3 =
  'EZRA-RSE Note 3 — "Roll 1d10. 1-7 Car gets loose in traffic, smacks wall. Roll for repair. If ' +
  'successful, displace to last position LLSG. No yellow flag. 8-0 Trouble in the field. Process ' +
  'under normal procedures as if MM designated Trouble on track card. This event cancels the ' +
  'regular turn action and ends the turn."';

/**
 * Note 3's sub-table. "8-0" is 8, 9 and 10 — a d10 whose tenth face prints as
 * `0`, which is how every d10 in this corpus is written.
 */
export type RseTroubleKind = 'wall' | 'field';
export function rseTroubleFor(face: number): RseTroubleKind {
  return face <= 7 ? 'wall' : 'field';
}

/** "Driver error. Lose 10 positions." */
export const DRIVER_ERROR_POSITIONS = 10;
/** "Position loss is 2D10 added (Minimum loss of 4 spots)." */
export const ROUGHED_UP_MINIMUM = 4;

// ---------------------------------------------------------------------------
// Results
// ---------------------------------------------------------------------------

export interface RestartEventResult extends RuleEvent {
  eventType: 'restart-event';
  momentTag: string;
  /** The d10 that decided the row. */
  roll: number;
  row: RseRow;
  kind: RseKind;
  /** The group the row's own d6 selected, or the group it names outright. */
  group: TroubleGroup | null;
  finder: DriverFinderResult | null;
  driverId: string | null;
  /** Note 3's sub-roll, on a Trouble result only. */
  troubleRoll: number | null;
  troubleKind: RseTroubleKind | null;
  /** Set when the RS trait cancelled the result outright. */
  rsTraitSaved: boolean;
  /**
   * "This event cancels the regular turn action and ends the turn." Only Note
   * 3's field-trouble branch does this.
   */
  cancelsTurn: boolean;
  /**
   * Row 1 — "No use of performance chips in the normal turn." Distinct from the
   * blanket prohibition during the RSE itself, which always applies.
   */
  chipsBannedThisTurn: boolean;
  /** The engine still has to run these; this module decides WHO and WHAT. */
  needsProblem: boolean;
  needsTrouble: boolean;
  needsRepair: boolean;
}

export interface RestartEventInput {
  field: TroubleField;
  dice: Dice;
  /** Driver ids carrying the EZRA RS trait. */
  restartExperts?: ReadonlySet<string>;
}

const GROUP_NAME: Record<TroubleGroup, string> = { top: 'TOP', middle: 'MIDDLE', bottom: 'BOTTOM' };

/**
 * Roll the trigger. Separate from resolution so a caller can ask "does an RSE
 * happen?" without paying for the rest, and so the rate is measurable on its
 * own.
 */
export function rollRseTrigger(dice: Dice): { fired: boolean; roll: number; record: DiceRecord } {
  const roll = dice.d10();
  return {
    fired: roll === RSE_TRIGGER_FACE,
    roll,
    record: {
      kind: 'd10',
      dice: [roll],
      total: roll,
      label: 'RSE trigger (prelude)',
      flags: { fired: roll === RSE_TRIGGER_FACE },
    },
  };
}

/**
 * Resolve a Race Start Event that has already been triggered.
 *
 * This module owns the TABLE — which row, which group, which driver, and what
 * the row asks for. It deliberately does NOT run the resulting problem, trouble
 * or repair: those live in `problem.ts`, `trouble.ts` and `repair.ts`, they need
 * the whole turn context, and duplicating any of them here is how two versions
 * of a rule start to drift. The result says what is needed; the turn runs it.
 */
export function resolveRestartEvent(input: RestartEventInput): RestartEventResult {
  const { dice, field } = input;
  const diceRecords: DiceRecord[] = [];
  const modifiers: Modifier[] = [];

  const roll = dice.d10();
  diceRecords.push({ kind: 'd10', dice: [roll], total: roll, label: 'RACE START RESULT TABLE' });
  const row = rseRowFor(roll);
  modifiers.push({ name: 'RSE result', value: row.text, detail: CITE_RSE });

  // ---- which group -------------------------------------------------------
  let group: TroubleGroup | null = null;
  if (row.kind === 'great') {
    group = 'top'; // "Use Top Group Driver Finder Table."
  } else if (row.groupDice) {
    const d6 = dice.d6();
    diceRecords.push({ kind: 'd6', dice: [d6], total: d6, label: 'RSE group' });
    group = row.groupDice[d6];
    modifiers.push({
      name: 'group',
      value: GROUP_NAME[group],
      detail: `d6 ${d6} — this row's own printed split, which is NOT the same on every row`,
    });
  }

  // ---- which driver ------------------------------------------------------
  //
  // "If the Designated Group or Driver is blank due to attrition, reroll on the
  // next higher group or on the DFT." `rollDriverFinder` already substitutes
  // the nearest occupied car inside a group and reports it; this walks UP a
  // group when the whole group is empty, which is the case the sheet names.
  let finder: DriverFinderResult | null = null;
  let driverId: string | null = null;
  if (group) {
    const ladder: TroubleGroup[] = group === 'bottom' ? ['bottom', 'middle', 'top'] : group === 'middle' ? ['middle', 'top'] : ['top'];
    for (const g of ladder) {
      if (field[g].length === 0) continue;
      finder = rollDriverFinder(g, field, dice);
      diceRecords.push(finder.dice);
      modifiers.push(...finder.modifiers);
      driverId = finder.car?.driverId ?? null;
      if (driverId) break;
    }
  }

  // ---- the RS trait ------------------------------------------------------
  //
  // "A driver with the RS Trait when INDIVIDUALLY DESIGNATED on the Race Start
  // Result Table ignores any negative result. This ends the RSE." So it needs a
  // named driver — row 1 hits the whole field and has nobody to designate, and
  // the trait cannot cancel it.
  const rsTraitSaved =
    row.negative && driverId !== null && (input.restartExperts?.has(driverId) ?? false);
  if (rsTraitSaved) {
    modifiers.push({ name: 'RS trait — Re-Start Expert', value: 'ignores the result', detail: CITE_RS_TRAIT });
  }

  // ---- Note 3's sub-roll -------------------------------------------------
  let troubleRoll: number | null = null;
  let troubleKind: RseTroubleKind | null = null;
  if (row.kind === 'trouble' && !rsTraitSaved) {
    troubleRoll = dice.d10();
    troubleKind = rseTroubleFor(troubleRoll);
    diceRecords.push({
      kind: 'd10',
      dice: [troubleRoll],
      total: troubleRoll,
      label: 'RSE TROUBLE (Note 3)',
    });
    modifiers.push({
      name: 'Note 3',
      value: troubleKind === 'wall' ? 'smacks the wall — no yellow' : 'trouble in the field — the turn ends',
      detail: CITE_NOTE_3,
    });
  }

  const who = finder?.car?.name ?? driverId ?? 'the field';
  const cancelsTurn = troubleKind === 'field';

  return {
    eventType: 'restart-event',
    momentTag: rsTraitSaved ? 'restart.rs-trait' : `restart.${row.kind}`,
    ruleCitation: `${CITE_RSE}; ${CITE_RSE_TRIGGER}`,
    actorIds: driverId ? [driverId] : [],
    positionDeltas: [] as PositionDelta[],
    dice: diceRecords,
    modifiers,
    description: rsTraitSaved
      ? `${who} is a Re-Start Expert and simply ignores it — ${row.text.toLowerCase()} The restart is clean.`
      : describeRse(row, troubleKind, who),
    roll,
    row,
    kind: row.kind,
    group,
    finder,
    driverId,
    troubleRoll,
    troubleKind,
    rsTraitSaved,
    cancelsTurn,
    chipsBannedThisTurn: row.kind === 'shaky',
    needsProblem: row.kind === 'problem' && !rsTraitSaved,
    needsTrouble: troubleKind === 'field',
    needsRepair: troubleKind === 'wall',
  };
}

function describeRse(row: RseRow, troubleKind: RseTroubleKind | null, who: string): string {
  switch (row.kind) {
    case 'shaky':
      return 'A shaky restart unsettles the whole field — nobody can spend a performance chip this turn.';
    case 'violation':
      return `${who} jumps the restart and is called for it — a stop and go.`;
    case 'great':
      return `${who} times the restart perfectly.`;
    case 'problem':
      return `${who} has a problem on the restart.`;
    case 'trouble':
      return troubleKind === 'wall'
        ? `${who} gets loose in traffic on the restart and smacks the wall. No caution.`
        : `Trouble in the field on the restart — and that is the turn gone.`;
    case 'driver-error':
      return `${who} makes a mess of the restart and loses ${DRIVER_ERROR_POSITIONS} positions.`;
    case 'roughed-up':
      return `${who} gets roughed up in traffic on the restart and goes backwards.`;
  }
}

/**
 * "Position loss is 2D10 added (Minimum loss of 4 spots)."
 *
 * Rolled here rather than in the caller so the minimum is applied in one place.
 * 2d10 already has a floor of 2, so the printed minimum of 4 only bites on a
 * roll of 2 or 3 — 3 outcomes in 100.
 */
export function roughedUpLoss(dice: Dice): { loss: number; rolls: [number, number] } {
  const [a, b] = dice.sum2d10();
  return { loss: Math.max(ROUGHED_UP_MINIMUM, a + b), rolls: [a, b] };
}

// ---------------------------------------------------------------------------
// The positional consequences
// ---------------------------------------------------------------------------

/**
 * What the notes actually do to the board.
 *
 * Kept here rather than in `turn.ts` so the rule and its citation stay in one
 * place, and returned as deltas rather than applied, so the turn loop's own
 * `applyDeltas` — which already knows about vacancies, chip forfeiture and the
 * laps-down clamp — is the single thing that ever moves a card.
 */
export interface RestartDeltas {
  deltas: PositionDelta[];
  /** Note 2 awards a performance chip rather than a move. */
  chipTo: string | null;
  note: string;
}

const none = (note: string): RestartDeltas => ({ deltas: [], chipTo: null, note });

export function restartPositionDeltas(
  result: RestartEventResult,
  order: readonly { card: { driverId: string }; position: number }[],
  dice: Dice,
): RestartDeltas {
  if (result.rsTraitSaved || !result.driverId) return none('nothing to move');
  const from = order.findIndex((c) => c.card.driverId === result.driverId) + 1;
  if (from === 0) return none('the designated driver is no longer on the board');
  const last = order.length;
  const move = (to: number, note: string): RestartDeltas => ({
    deltas: [{ driverId: result.driverId as string, from, to: Math.min(Math.max(2, to), last) }],
    chipTo: null,
    note,
  });

  switch (result.kind) {
    case 'violation':
      // Note 1 — "Top Group offenders displace to the last position in the
      // middle group. Others to the last position in the Bottom Group Lead Lap
      // Sub-Group."
      //
      // AMBIGUITY: the LLSG's last position is only distinct from the end of
      // the field when the laps-down rule is running and somebody is actually
      // down. `turn.ts` owns that boundary and its `applyDeltas` clamps a move
      // that would cross the laps-down marker, so the end of the field is the
      // right target to ASK for and the clamp is what makes it correct.
      return from <= 7
        ? move(19, 'Note 1 — TOP group offender to the last MIDDLE place')
        : move(last, 'Note 1 — to the last place in the Bottom Group lead-lap sub-group');

    case 'great': {
      // Note 2 — "If either the Leader or second place car is designated ...
      // award a performance chip. If the designated car is in 3rd through 7th
      // place, it advances to second place."
      if (from <= 2) {
        return { deltas: [], chipTo: result.driverId, note: 'Note 2 — a performance chip' };
      }
      if (from >= 3 && from <= 7) {
        // "The Second-place car displaces to the vacated spot" — an exchange,
        // not an insertion, which is why both cars carry a delta.
        const second = order[1];
        return {
          deltas: [
            { driverId: result.driverId, from, to: 2 },
            ...(second ? [{ driverId: second.card.driverId, from: 2, to: from }] : []),
          ],
          chipTo: null,
          note: 'Note 2 — up to second, swapping with the car that was there',
        };
      }
      // Designated outside 1st-7th: the note covers only the leader, second and
      // 3rd-7th, so there is nothing printed for a car further back. Nothing
      // happens, and it says so rather than inventing a reward.
      return none('Note 2 covers 1st-7th only; the designated car is further back');
    }

    case 'driver-error':
      return move(from + DRIVER_ERROR_POSITIONS, `driver error — ${DRIVER_ERROR_POSITIONS} positions`);

    case 'roughed-up': {
      const { loss, rolls } = roughedUpLoss(dice);
      return move(from + loss, `roughed up — 2d10 (${rolls.join('+')}) = ${loss} positions`);
    }

    // Shaky hits nobody; problem, trouble and repair are run by the modules
    // that own them and produce their own deltas.
    case 'shaky':
    case 'problem':
    case 'trouble':
      return none('resolved by the module that owns it');
  }
}

// ---------------------------------------------------------------------------
// The RESTART DUEL — the other half of the RS trait
// ---------------------------------------------------------------------------

/**
 * EZRA-S2 §4.0, Re-Start Expert, verbatim:
 *
 *   *"Any time a race restarts after a Yellow Flag, this driver gets an extra
 *    event at the start of the turn if the driver is in the group identified by
 *    the race cam dice. Before the Normal turn events this driver has the option
 *    to duel anybody within the same group. If this driver wins the Restart Duel
 *    the driver will still be eligible for the normal turn processing. If the
 *    driver fails the duel that completes their turn. If the driver passes on
 *    the duel continue to normal turn processing. If the RS driver is
 *    individually designated on the Race Start Result Table during an RSE, the
 *    negative RSE result is ignored and the RSE ends."*
 *
 * The last sentence is the RSE override, already implemented above. This is the
 * rest, and it is a SEPARATE MECHANIC from the Race Start Event — worth stating
 * plainly because both live under the word "restart" and it would be easy to
 * assume one gates the other:
 *
 * | | Race Start Event | Restart Duel |
 * |---|---|---|
 * | when | first turn, or after a yellow | **after a yellow only** |
 * | gate | a d10 of 1 (10%) | **none — it always offers** |
 * | needs | nothing | an RS driver **in the called group** |
 *
 * So the duel is offered on restarts where no RSE fired at all, and the first
 * turn of a race — a restart in the RSE's sense — is **not** one for this rule,
 * because a race that has not started cannot "restart after a Yellow Flag".
 *
 * ── THREE THINGS THE SENTENCE SETTLES THAT LOOK OPEN ───────────────────────
 *
 * 1. **It needs the race cam first.** *"if the driver is in the group identified
 *    by the race cam dice"* — so the cam is read, and only then does the RS
 *    driver get his extra event, still "before the Normal turn events". On a PIT
 *    turn there is no race cam at all (RB/xi), so there is no group to be in and
 *    no restart duel. That falls out of the rule rather than being a ruling.
 *
 * 2. **A loss ends his turn, not the turn.** *"that completes **their** turn"* —
 *    singular possessive, about the RS driver. Implemented through
 *    `excludeChallengers` on the challenge sequence.
 *
 * 3. **It is optional, and losing is expensive.** A duel loser takes TROUBLE
 *    (RB/p.ix) AND forfeits the rest of his turn. So "the option to duel" is a
 *    real decision rather than free money.
 *
 * ── THE ONE RULING: WHAT DOES A SOLITAIRE RS DRIVER DECIDE? ────────────────
 *
 * D-EZ-23. The sheet gives an option and no policy, exactly as RB/p.xiv does
 * for chips — and the base rulebook answers that one with a solitaire
 * simplification (*"a driver who HAS a performance chip automatically SPENDS
 * it"*). The same shape of answer is used here: **the RS driver takes the duel
 * whenever he has a target.**
 *
 * Reasoning, and it is a rate argument rather than a taste one: a trait that
 * declines is a trait that never appears, and RS is dealt to about one driver in
 * a league. Declining by default would make the whole clause unobservable. The
 * policy is a parameter (`restartDuelPolicy`) so a head-to-head game can decide
 * it by hand, and `'never'` restores a league where RS is purely the RSE
 * override.
 */

export const CITE_RS_DUEL =
  'EZRA-S2 §4.0 Re-Start Expert — "Any time a race restarts after a Yellow Flag, this driver gets ' +
  'an extra event at the start of the turn if the driver is in the group identified by the race cam ' +
  'dice. Before the Normal turn events this driver has the option to duel anybody within the same ' +
  'group. If this driver wins the Restart Duel the driver will still be eligible for the normal ' +
  'turn processing. If the driver fails the duel that completes their turn. If the driver passes ' +
  'on the duel continue to normal turn processing."';

export const CITE_D_EZ_23 =
  'D-EZ-23 — EZRA gives the RS driver "the option to duel" and no policy. Read the way RB/p.xiv ' +
  'answers the same shape of question for performance chips ("a driver who HAS a performance chip ' +
  'automatically SPENDS it"): the solitaire default is to TAKE the duel whenever a target exists. ' +
  'A trait that declines is a trait that never appears. Parameterised as `restartDuelPolicy`.';

/** Whether the RS driver takes his free duel. See D-EZ-23. */
export type RestartDuelPolicy = 'auto' | 'never';

export interface RestartDuelOffer {
  /** The RS driver, or null when nobody qualifies this turn. */
  instigatorId: string | null;
  /** Why not, when there is no offer — reported rather than silently skipped. */
  reason: string;
}

/**
 * Is a restart duel on the table this turn, and for whom?
 *
 * Deliberately pure and separate from resolving it: the eligibility rule has
 * four printed conditions and every one of them is a place to get it wrong
 * quietly, so it is testable on its own.
 */
export function restartDuelOffer(args: {
  /** Does THIS turn immediately follow a yellow flag turn? */
  followsYellow: boolean;
  /** The group the race cam called, or null on a pit or yellow turn. */
  calledGroup: TroubleGroup | null;
  /** Position of each RS driver still running, by driver id. */
  restartExpertPositions: ReadonlyMap<string, number>;
  /** Which group a position falls in. */
  groupOfPosition: (position: number) => TroubleGroup;
}): RestartDuelOffer {
  if (!args.followsYellow) {
    return { instigatorId: null, reason: 'the turn does not follow a yellow flag' };
  }
  if (args.calledGroup === null) {
    // "the group identified by the race cam dice" — no cam, no group, no duel.
    return { instigatorId: null, reason: 'no race cam was read this turn' };
  }
  for (const [driverId, position] of args.restartExpertPositions) {
    if (args.groupOfPosition(position) === args.calledGroup) {
      return { instigatorId: driverId, reason: '' };
    }
  }
  return {
    instigatorId: null,
    reason: args.restartExpertPositions.size === 0
      ? 'no Re-Start Expert is running'
      : 'the Re-Start Expert is not in the group the race cam called',
  };
}
